<script setup lang="ts">
import { nextTick, ref, watch } from "vue";

import { mentionTokenFor } from "@/lib/discussion-mentions";
import type { MentionableEntity } from "@/types";

interface Props {
  modelValue?: string;
  mentionedUserIds?: number[];
  mentionedGroupIds?: number[];
  mentionables?: MentionableEntity[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  mentionedUserIds: () => [],
  mentionedGroupIds: () => [],
  mentionables: () => [],
  placeholder: "",
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "update:mentionedUserIds": [value: number[]];
  "update:mentionedGroupIds": [value: number[]];
  mentionQuery: [query: string];
  mentionClear: [];
}>();

const editorRef = ref<HTMLDivElement | null>(null);
const isFocused = ref(false);
const isEmpty = ref(!props.modelValue);

defineExpose({ editorRef, insertMention, focus });

function getPlainText(): string {
  if (!editorRef.value) return "";
  const cloned = editorRef.value.cloneNode(true) as HTMLElement;
  cloned.querySelectorAll("[data-mention-token]").forEach((el) => {
    el.replaceWith(el.getAttribute("data-mention-token") || "");
  });
  return (cloned.innerText || cloned.textContent || "").replace(/\n$/, "");
}

function syncModelValue() {
  const text = getPlainText();
  isEmpty.value = !text.trim();
  emit("update:modelValue", text);
  syncMentionIds();
}

function syncMentionIds() {
  if (!editorRef.value) {
    emit("update:mentionedUserIds", []);
    emit("update:mentionedGroupIds", []);
    return;
  }

  const userIds = new Set<number>();
  const groupIds = new Set<number>();

  editorRef.value
    .querySelectorAll<HTMLElement>("[data-mention-id][data-mention-type]")
    .forEach((element) => {
      const rawId = Number.parseInt(element.dataset.mentionId || "", 10);
      if (Number.isNaN(rawId) || rawId <= 0) return;

      if (element.dataset.mentionType === "user") {
        userIds.add(rawId);
      } else if (element.dataset.mentionType === "group") {
        groupIds.add(rawId);
      }
    });

  emit("update:mentionedUserIds", [...userIds]);
  emit("update:mentionedGroupIds", [...groupIds]);
}

function handleInput() {
  syncModelValue();
  detectMentionQuery();
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Enter" && !event.shiftKey) {
    // allow shift+enter for newlines, plain enter does nothing special in description
  }
}

function detectMentionQuery() {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !editorRef.value) {
    emit("mentionClear");
    return;
  }

  const range = sel.getRangeAt(0);
  if (!editorRef.value.contains(range.startContainer)) {
    emit("mentionClear");
    return;
  }

  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) {
    emit("mentionClear");
    return;
  }

  const textBefore = (node.textContent || "").slice(0, range.startOffset);
  const atIndex = textBefore.lastIndexOf("@");

  if (atIndex < 0) {
    emit("mentionClear");
    return;
  }

  const charBeforeAt = textBefore[atIndex - 1];
  if (charBeforeAt && !/[\s([{]/.test(charBeforeAt)) {
    emit("mentionClear");
    return;
  }

  const query = textBefore.slice(atIndex + 1);
  if (/[\s\n]/.test(query)) {
    emit("mentionClear");
    return;
  }

  emit("mentionQuery", query.toLowerCase());
}

function insertMention(entity: MentionableEntity) {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !editorRef.value) return;

  const range = sel.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return;

  const textBefore = (node.textContent || "").slice(0, range.startOffset);
  const atIndex = textBefore.lastIndexOf("@");
  if (atIndex < 0) return;

  const textAfter = (node.textContent || "").slice(range.startOffset);
  const beforeAt = textBefore.slice(0, atIndex);

  // Build badge element
  const badge = document.createElement("span");
  badge.setAttribute("contenteditable", "false");
  badge.setAttribute("data-mention-token", mentionTokenFor(entity));
  badge.setAttribute("data-mention-type", entity.type);
  badge.setAttribute("data-mention-id", String(entity.id));
  badge.className =
    "mention-badge inline-flex items-center rounded-full border border-transparent bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground align-baseline";
  badge.textContent = `@${entity.name}`;

  // Replace text node content
  const parent = node.parentNode!;
  const beforeNode = document.createTextNode(beforeAt);
  const afterNode = document.createTextNode(`\u00A0${textAfter}`);

  parent.insertBefore(beforeNode, node);
  parent.insertBefore(badge, node);
  parent.insertBefore(afterNode, node);
  parent.removeChild(node);

  // Set cursor after the space
  const newRange = document.createRange();
  newRange.setStart(afterNode, 1);
  newRange.collapse(true);
  sel.removeAllRanges();
  sel.addRange(newRange);

  emit("mentionClear");
  syncModelValue();
}

function focus() {
  editorRef.value?.focus();
}

// Sync initial value into contenteditable on mount
watch(
  () => props.modelValue,
  (val) => {
    if (!editorRef.value) return;
    const current = getPlainText();
    if (current === val) return;
    editorRef.value.textContent = val;
    isEmpty.value = !val.trim();
    syncMentionIds();
  },
  { immediate: true },
);

// After mount, set initial content
async function initContent() {
  await nextTick();
  if (editorRef.value && props.modelValue && !editorRef.value.textContent?.trim()) {
    editorRef.value.textContent = props.modelValue;
    isEmpty.value = !props.modelValue.trim();
  }
  syncMentionIds();
}
void initContent();
</script>

<template>
  <div class="mention-editor-wrap relative">
    <div
      ref="editorRef"
      contenteditable="true"
      role="textbox"
      :aria-placeholder="placeholder"
      class="mention-editor border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border bg-background/90 px-3 py-2 text-base shadow-xs outline-none transition-[color,box-shadow] focus-visible:ring-[3px] md:text-sm"
      @input="handleInput"
      @keydown="handleKeydown"
      @click="detectMentionQuery"
      @keyup="detectMentionQuery"
      @focus="isFocused = true"
      @blur="isFocused = false"
    />
    <span
      v-if="isEmpty && !isFocused"
      class="pointer-events-none absolute left-3 top-2 text-base text-muted-foreground md:text-sm"
    >
      {{ placeholder }}
    </span>
    <input type="hidden" name="discussion_thread[description]" :value="modelValue" />
  </div>
</template>

<style scoped>
.mention-editor {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-y: auto;
}


.mention-editor :deep(.mention-badge) {
  user-select: all;
  cursor: default;
}
</style>
